import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, finalize } from 'rxjs';
import {
  SwipeService,
  SwipeListing,
  SwipeStats,
} from '../services/swipe.service';
import { AuthService } from '../services/auth.service';
import { WishlistService } from '../services/wishlist.service';

@Component({
  selector: 'app-swipe',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './swipe.component.html',
  styleUrls: ['./swipe.component.css'],
})
export class SwipeComponent implements OnInit, OnDestroy {
  @ViewChild('swipeCard', { static: false }) swipeCard!: ElementRef;

  listings: SwipeListing[] = [];
  currentIndex: number = 0;
  currentListing: SwipeListing | null = null;
  currentImageIndex: number = 0;
  stats: SwipeStats = {
    session_viewed_count: 0,
    total_wishlist_count: 0,
    wishlist_by_category: {},
  };

  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isProcessingAction: boolean = false;
  isAnimating: boolean = false;
  isRefreshing: boolean = false;
  hasMoreListings: boolean = true;
  error?: string;

  selectedCategories: string[] = [];
  availableCategories = [
    { key: 'vehicles', label: 'Vehicles', icon: 'car' },
    { key: 'real-estate', label: 'Real Estate', icon: 'home' },
    { key: 'marketplace', label: 'Marketplace', icon: 'shopping-bag' },
    { key: 'jobs', label: 'Jobs', icon: 'briefcase' },
  ];

  isMobile: boolean = false;
  isDragging: boolean = false;
  showFiltersPanel: boolean = false;
  showStatsPanel: boolean = false;

  isCurrentListingWishlisted: boolean = false;
  isProcessingHeart: boolean = false;
  private heartTouchStartTime: number = 0;
  private heartTouchStartX: number = 0;
  private heartTouchStartY: number = 0;

  private startX: number = 0;
  private startY: number = 0;
  private currentX: number = 0;
  private currentY: number = 0;
  private swipeThreshold: number = 80;
  private velocityThreshold: number = 0.3;
  private lastMoveTime: number = 0;
  private animationFrame: number = 0;
  private hasMouseListeners: boolean = false;
  private imageTouchStartTime: number = 0;
  private imageTouchStartX: number = 0;
  private imageTouchStartY: number = 0;
  private cardDragStarted: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private swipeService: SwipeService,
    private authService: AuthService,
    private wishlistService: WishlistService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }

    this.checkIsMobile();
    window.addEventListener('resize', this.onResize.bind(this));

    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.cleanup();
    window.removeEventListener('resize', this.onResize.bind(this));
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkIsMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private onResize(): void {
    this.checkIsMobile();
  }

  private cleanup(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.removeMouseListeners();
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.error = undefined;

    this.swipeService
      .getSwipeStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.stats = stats;
        },
        error: (error) => {
          console.warn('Failed to load swipe stats:', error);
        },
      });

    if (this.authService.isLoggedIn()) {
      this.wishlistService.refreshWishlistSummary();
    }

    this.loadMoreListings();
  }

  loadMoreListings(): void {
    if (this.isLoadingMore || !this.hasMoreListings) return;

    this.isLoadingMore = true;

    this.swipeService
      .getSwipeFeed(
        15,
        this.selectedCategories.length > 0 ? this.selectedCategories : undefined
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe({
        next: (response) => {
          const newListings = response.data.listings;
          this.listings = [...this.listings, ...newListings];
          this.hasMoreListings = response.data.has_more;
          this.stats.session_viewed_count =
            response.data.total_viewed_in_session;

          if (this.currentListing === null && this.listings.length > 0) {
            this.currentIndex = 0;
            this.currentListing = this.listings[0];
            this.currentImageIndex = 0;
            this.preloadImages(this.currentListing);
            this.checkCurrentListingWishlistStatus();
          }

          this.error = undefined;
        },
        error: (error) => {
          this.error = error.message;
        },
      });
  }

  moveToNextListing(): void {
    this.currentIndex++;
    this.currentImageIndex = 0;

    if (this.currentIndex >= this.listings.length) {
      if (this.hasMoreListings) {
        this.currentListing = null;
        this.loadMoreListings();
      } else {
        this.currentListing = null;
      }
      this.isAnimating = false;
    } else {
      this.currentListing = null;

      requestAnimationFrame(() => {
        this.currentListing = this.listings[this.currentIndex];
        this.preloadImages(this.currentListing);
        this.checkCurrentListingWishlistStatus();
        this.addCardEntranceAnimation();
        this.isAnimating = false;
      });
    }
  }

  moveToPreviousListing(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.currentListing = null;

      requestAnimationFrame(() => {
        this.currentListing = this.listings[this.currentIndex];
        this.currentImageIndex = 0;
        this.preloadImages(this.currentListing);
        this.checkCurrentListingWishlistStatus();
        this.addCardEntranceAnimation();
      });
    } else {
      this.showToast('Already at the first listing');
      this.resetCardPosition();
    }
  }

  toggleCategory(categoryKey: string): void {
    const index = this.selectedCategories.indexOf(categoryKey);
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
    } else {
      this.selectedCategories.push(categoryKey);
    }

    this.listings = [];
    this.currentIndex = 0;
    this.currentListing = null;
    this.hasMoreListings = true;

    this.loadMoreListings();
  }

  resetSession(): void {
    this.swipeService
      .resetSwipeSession()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.listings = [];
          this.currentIndex = 0;
          this.currentListing = null;
          this.hasMoreListings = true;
          this.stats.session_viewed_count = 0;
          this.loadInitialData();
        },
        error: (error) => {
          this.showErrorFeedback('Failed to reset session');
        },
      });
  }

  nextImage(event?: Event): void {
    if (event) event.stopPropagation();
    if (
      !this.currentListing ||
      !this.currentListing.images ||
      this.currentListing.images.length <= 1
    )
      return;

    this.currentImageIndex =
      (this.currentImageIndex + 1) % this.currentListing.images.length;
  }

  previousImage(event?: Event): void {
    if (event) event.stopPropagation();
    if (
      !this.currentListing ||
      !this.currentListing.images ||
      this.currentListing.images.length <= 1
    )
      return;

    this.currentImageIndex =
      this.currentImageIndex === 0
        ? this.currentListing.images.length - 1
        : this.currentImageIndex - 1;
  }

  goToImage(index: number, event: Event): void {
    event.stopPropagation();
    if (!this.currentListing || !this.currentListing.images) return;

    this.currentImageIndex = index;
  }

  getCurrentImage(): string {
    if (
      !this.currentListing ||
      !this.currentListing.images ||
      this.currentListing.images.length === 0
    ) {
      return this.getDefaultImage(this.currentListing!);
    }
    return this.currentListing.images[this.currentImageIndex];
  }

  getImageArray(): any[] {
    if (!this.currentListing) return [];
    return new Array(this.currentListing.image_count);
  }

  onImageTouchStart(event: TouchEvent, direction: 'left' | 'right'): void {
    this.imageTouchStartTime = Date.now();
    this.imageTouchStartX = event.touches[0].clientX;
    this.imageTouchStartY = event.touches[0].clientY;
    this.cardDragStarted = false;
  }

  onImageTouchEnd(event: TouchEvent, direction: 'left' | 'right'): void {
    if (this.cardDragStarted) {
      return;
    }

    const touchDuration = Date.now() - this.imageTouchStartTime;
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;

    const deltaX = Math.abs(touchEndX - this.imageTouchStartX);
    const deltaY = Math.abs(touchEndY - this.imageTouchStartY);

    if (touchDuration < 300 && deltaX < 10 && deltaY < 10) {
      event.preventDefault();
      event.stopPropagation();

      if (direction === 'left') {
        this.previousImage();
      } else {
        this.nextImage();
      }
    }
  }

  onDotTouchStart(event: TouchEvent, index: number): void {
    this.imageTouchStartTime = Date.now();
    this.imageTouchStartX = event.touches[0].clientX;
    this.imageTouchStartY = event.touches[0].clientY;
  }

  onDotTouchEnd(event: TouchEvent, index: number): void {
    const touchDuration = Date.now() - this.imageTouchStartTime;
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;

    const deltaX = Math.abs(touchEndX - this.imageTouchStartX);
    const deltaY = Math.abs(touchEndY - this.imageTouchStartY);

    if (touchDuration < 300 && deltaX < 10 && deltaY < 10) {
      event.preventDefault();
      event.stopPropagation();
      this.goToImage(index, event);
    }
  }

  onHeartTouchStart(event: TouchEvent): void {
    this.heartTouchStartTime = Date.now();
    this.heartTouchStartX = event.touches[0].clientX;
    this.heartTouchStartY = event.touches[0].clientY;
  }

  onHeartTouchEnd(event: TouchEvent): void {
    const touchDuration = Date.now() - this.heartTouchStartTime;
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;

    const deltaX = Math.abs(touchEndX - this.heartTouchStartX);
    const deltaY = Math.abs(touchEndY - this.heartTouchStartY);

    if (touchDuration < 300 && deltaX < 10 && deltaY < 10) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleWishlistFromHeart(event);
    }
  }

  toggleWishlistFromHeart(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (!this.currentListing || this.isProcessingHeart) return;

    this.isProcessingHeart = true;
    this.triggerHapticFeedback();

    this.wishlistService
      .toggleWishlist(this.currentListing.id, this.currentListing.table_name)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isProcessingHeart = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.isCurrentListingWishlisted = response.data.in_wishlist;

          if (response.data.action === 'added') {
            this.stats.total_wishlist_count++;
            this.showToast('Added to wishlist! ❤️');
          } else {
            this.stats.total_wishlist_count--;
            this.showToast('Removed from wishlist');
          }

          this.wishlistService.refreshWishlistSummary();
        },
        error: (error) => {
          this.showErrorFeedback(error.message);
        },
      });
  }

  private checkCurrentListingWishlistStatus(): void {
    if (!this.currentListing) return;

    this.wishlistService
      .checkWishlistStatus([
        {
          id: this.currentListing.id,
          table_name: this.currentListing.table_name,
        },
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          this.isCurrentListingWishlisted =
            status[this.currentListing!.id]?.in_wishlist || false;
        },
        error: (error) => {
          this.isCurrentListingWishlisted = false;
        },
      });
  }

  onTouchStart(event: TouchEvent): void {
    if (this.isProcessingAction || this.isAnimating) return;

    const touch = event.touches[0];
    this.startDrag(touch.clientX, touch.clientY);
    this.cardDragStarted = false;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.startX);
    const deltaY = Math.abs(touch.clientY - this.startY);

    if (deltaX > 10 || deltaY > 10) {
      this.cardDragStarted = true;
    }

    this.updateDrag(touch.clientX, touch.clientY);

    if (deltaX > 10 || deltaY > 10) {
      event.preventDefault();
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (!this.isDragging) return;

    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;

    this.endDrag();
    this.determineSwipeAction(deltaX, deltaY);
  }

  onMouseDown(event: MouseEvent): void {
    if (this.isProcessingAction || this.isAnimating || this.isMobile) return;

    event.preventDefault();
    this.startDrag(event.clientX, event.clientY);
    this.addMouseListeners();
  }

  private addMouseListeners(): void {
    if (this.hasMouseListeners) return;

    this.hasMouseListeners = true;
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private removeMouseListeners(): void {
    if (!this.hasMouseListeners) return;

    this.hasMouseListeners = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging) return;
    this.updateDrag(event.clientX, event.clientY);
    event.preventDefault();
  };

  private onMouseUp = (event: MouseEvent): void => {
    if (!this.isDragging) return;
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    this.endDrag();
    this.removeMouseListeners();
    this.determineSwipeAction(deltaX, deltaY);
    event.preventDefault();
  };

  private startDrag(clientX: number, clientY: number): void {
    this.startX = clientX;
    this.startY = clientY;
    this.currentX = clientX;
    this.currentY = clientY;
    this.isDragging = true;
    this.lastMoveTime = Date.now();

    if (this.swipeCard?.nativeElement) {
      this.swipeCard.nativeElement.style.cursor = 'grabbing';
      this.swipeCard.nativeElement.style.transition = 'none';
    }
  }

  private updateDrag(clientX: number, clientY: number): void {
    if (!this.isDragging) return;

    this.currentX = clientX;
    this.currentY = clientY;
    this.lastMoveTime = Date.now();

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = requestAnimationFrame(() => {
      this.updateCardPosition();
    });
  }

  private endDrag(): void {
    if (!this.isDragging) return;

    this.isDragging = false;

    if (this.swipeCard?.nativeElement) {
      this.swipeCard.nativeElement.style.cursor = 'grab';
    }
  }

  private determineSwipeAction(deltaX: number, deltaY: number): void {
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaX < this.swipeThreshold && absDeltaY < this.swipeThreshold) {
      this.resetCardPosition();
      return;
    }

    if (absDeltaX > absDeltaY) {
      if (deltaX < 0) {
        this.handleLeftSwipe();
      } else {
        this.resetCardPosition();
      }
    } else {
      if (deltaY < 0) {
        this.handleUpSwipe();
      } else {
        this.handleDownSwipe();
      }
    }
  }

  private handleLeftSwipe(): void {
    this.animateCardExit('left', () => {
      this.viewListing(this.currentListing);
    });
  }

  private handleUpSwipe(): void {
    this.animateCardExit('up', () => {
      this.moveToNextListing();
    });
  }

  private handleDownSwipe(): void {
    if (this.currentIndex === 0) {
      this.isRefreshing = true;
      this.animateCardExit('down', () => {
        setTimeout(() => {
          this.listings = [];
          this.currentIndex = 0;
          this.currentListing = null;
          this.hasMoreListings = true;
          this.loadMoreListings();

          setTimeout(() => {
            this.isRefreshing = false;
          }, 500);
        }, 800);
      });
      return;
    }

    this.animateCardExit('down', () => {
      this.moveToPreviousListing();
    });
  }

  private updateCardPosition(): void {
    if (!this.swipeCard?.nativeElement || !this.isDragging) return;

    let deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;

    if (deltaX > 0) {
      deltaX = 0;
    }

    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    const maxRotation = 15;
    const rotation = Math.max(
      -maxRotation,
      Math.min(maxRotation, deltaX * 0.08)
    );
    const opacity = Math.max(0.7, 1 - (absDeltaX + absDeltaY) / 600);

    const element = this.swipeCard.nativeElement;
    element.style.transform = `translate(${deltaX}px, ${deltaY}px) rotate(${rotation}deg)`;
    element.style.opacity = opacity.toString();
  }

  private resetCardPosition(): void {
    if (!this.swipeCard?.nativeElement) return;

    const element = this.swipeCard.nativeElement;
    element.style.transition = 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    element.style.transform = 'translate(0, 0) rotate(0deg)';
    element.style.opacity = '1';
    element.style.cursor = 'grab';

    setTimeout(() => {
      if (this.swipeCard?.nativeElement) {
        this.swipeCard.nativeElement.style.transition = '';
      }
    }, 300);
  }

  private animateCardExit(
    direction: 'left' | 'right' | 'up' | 'down',
    callback: () => void
  ): void {
    if (!this.swipeCard?.nativeElement) {
      callback();
      return;
    }

    const element = this.swipeCard.nativeElement;
    const distance = window.innerWidth * 1.5;

    let transform = '';
    if (direction === 'left') {
      transform = `translateX(-${distance}px) rotate(-30deg)`;
    } else if (direction === 'right') {
      transform = `translateX(${distance}px) rotate(30deg)`;
    } else if (direction === 'up') {
      transform = `translateY(-${distance}px)`;
    } else {
      transform = `translateY(${distance}px)`;
    }

    element.style.transition =
      'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    element.style.transform = transform;
    element.style.opacity = '0';

    setTimeout(() => {
      callback();
    }, 400);
  }

  private addCardEntranceAnimation(): void {
    if (!this.swipeCard?.nativeElement) return;

    const element = this.swipeCard.nativeElement;
    element.style.transition = '';
    element.style.transform = 'scale(0.8)';
    element.style.opacity = '0';

    requestAnimationFrame(() => {
      element.style.transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
      element.style.transform = 'scale(1)';
      element.style.opacity = '1';

      setTimeout(() => {
        element.style.transition = '';
      }, 300);
    });
  }

  private preloadImages(listing: SwipeListing | null): void {
    if (!listing || !listing.images || listing.images.length === 0) return;

    listing.images.forEach((imageUrl) => {
      const img = new Image();
      img.src = imageUrl;
    });
  }

  toggleFiltersPanel(): void {
    this.showFiltersPanel = !this.showFiltersPanel;
    if (this.showFiltersPanel) {
      this.showStatsPanel = false;
    }
  }

  toggleStatsPanel(): void {
    this.showStatsPanel = !this.showStatsPanel;
    if (this.showStatsPanel) {
      this.showFiltersPanel = false;
    }
  }

  closeAllPanels(): void {
    this.showFiltersPanel = false;
    this.showStatsPanel = false;
  }

  viewListing(listing: SwipeListing | null): void {
    if (!listing) return;

    const category = this.getCategoryFromTableName(listing.table_name);
    const subcategory = this.getSubcategoryFromTableName(listing.table_name);

    this.router.navigate(['/listing-details', subcategory, listing.id]);
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.currentListing || this.isProcessingAction || this.isAnimating)
      return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.handleLeftSwipe();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.handleUpSwipe();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.handleDownSwipe();
        break;
      case 'Enter':
        event.preventDefault();
        this.viewListing(this.currentListing);
        break;
    }
  }

  private showErrorFeedback(message: string): void {
    this.showToast(message);
  }

  private showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 2000);
  }

  private triggerHapticFeedback(): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }

  formatPrice(price: number, currency: string): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  }

  getCategoryDisplayName(tableName: string): string {
    const category = this.getCategoryFromTableName(tableName);
    const categoryNames: { [key: string]: string } = {
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryNames[category] || 'Other';
  }

  getCategoryFromTableName(tableName: string): string {
    if (
      tableName.includes('automobile') ||
      tableName.includes('motorcycle') ||
      tableName.includes('commercial') ||
      tableName.includes('oldtimer') ||
      tableName.includes('nautical') ||
      tableName.includes('spare_parts') ||
      tableName.includes('campers') ||
      tableName.includes('crashed') ||
      tableName.includes('construction') ||
      tableName.includes('agriculture')
    ) {
      return 'vehicles';
    } else if (
      tableName.includes('house') ||
      tableName.includes('apartment') ||
      tableName.includes('land') ||
      tableName.includes('office') ||
      tableName.includes('garage') ||
      tableName.includes('luxury') ||
      tableName.includes('room') ||
      tableName.includes('roommate')
    ) {
      return 'real-estate';
    } else if (
      tableName.includes('professional') ||
      tableName.includes('maintenance') ||
      tableName.includes('personal_care') ||
      tableName.includes('events') ||
      tableName.includes('education') ||
      tableName.includes('transport')
    ) {
      return 'jobs';
    } else {
      return 'marketplace';
    }
  }

  getSubcategoryFromTableName(tableName: string): string {
    const subcategoryMap: { [key: string]: string } = {
      attributes_automobile: 'automobile',
      attributes_motorcycle: 'motorcycle',
      attributes_commercial_vehicles: 'commercial-vehicles',
      attributes_oldtimer: 'oldtimer',
      attributes_nautical: 'nautical',
      attributes_spare_parts: 'spare-parts',
      attributes_campers_caravans: 'campers-caravans',
      attributes_crashed_cars: 'crashed-cars',
      attributes_construction: 'construction',
      attributes_agriculture: 'agriculture',
      attributes_house_sale: 'house-sale',
      attributes_house_rent: 'house-rent',
      attributes_apartments_sale: 'apartments-sale',
      attributes_apartments_rent: 'apartments-rent',
      attributes_land_sale: 'land-sale',
      attributes_land_rent: 'land-rent',
      attributes_business_office_sale: 'business-office-sale',
      attributes_business_office_rent: 'business-office-rent',
      attributes_garage_sale: 'garage-sale',
      attributes_garage_rent: 'garage-rent',
      attributes_luxury_real_estate_sale: 'luxury-real-estate-sale',
      attributes_luxury_real_estate_rent: 'luxury-real-estate-rent',
      attributes_room_rent: 'room-rent',
      attributes_roommate: 'looking-for-roommate',
      attributes_home: 'home',
      attributes_electronics: 'electronics',
      attributes_sport: 'sport',
      attributes_children: 'children',
      attributes_head_to_toe: 'head-to-toe',
      attributes_machines_tools: 'machines-tools',
      attributes_media: 'media',
      attributes_musical_instruments: 'musical-instruments',
      attributes_literature: 'literature',
      attributes_pets: 'pets',
      attributes_other: 'other',
      attributes_giving_away: 'giving-away',
      attributes_professional: 'professional',
      attributes_home_maintenance: 'home-maintenance',
      attributes_personal_care_wellness: 'personal-care-wellness',
      attributes_events_entertainment: 'events-entertainment',
      attributes_education_training: 'education-training',
      attributes_transport_delivery: 'transport-delivery',
    };

    return (
      subcategoryMap[tableName] ||
      tableName.replace('attributes_', '').replace(/_/g, '-')
    );
  }

  getDefaultImage(listing: SwipeListing): string {
    const category = this.getCategoryFromTableName(listing.table_name);
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/listing-placeholder/no-image-placeholder.png',
      'real-estate':
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      marketplace:
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      jobs: '/assets/images/listing-placeholder/no-image-placeholder.png',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  onImageError(event: any): void {
    if (this.currentListing) {
      event.target.src = this.getDefaultImage(this.currentListing);
    }
  }

  isNewListing(listing: SwipeListing): boolean {
    if (!listing || !listing.created_at) return false;

    const createdDate = new Date(listing.created_at);
    const now = new Date();
    const daysSinceCreation = Math.floor(
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysSinceCreation <= 3;
  }
}
